import { stringToSlug } from '../../utils/string.utils';
import { LayerDto, LayerDuplicateOptions, LayerGroupDto, LayerType } from '../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { createLayoutGroupDefaults } from '../../../../../../api/src/creative-unit-package/models/layer/layout-group.dto';
import { CustomizationItemDto } from '../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { CreativeUnitDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';
import {
	CreativeUnitPackageDto,
	StylesContainerConfig
} from '../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { merge } from 'lodash';
import { shapeGallery } from './ad.data';
import { CreativePackageUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-package.utils';
import { unfreeze } from '../../utils/object.utils';
import { LayersUtils } from '../../../../../../api/src/creative-unit-package/utils/layers.utils';
import { ExportType } from "../../../../../../api/src/encode/models/job-def.dto";

export class AdUtils {
	/**
	 * Creates a blank creative unit package entity.
	 */
	public static createCreativeUnitPackage(overrides?: any, cloneFrom?: CreativeUnitPackageDto): CreativeUnitPackageDto {
		let schema = {
			id: overrides?.id || new Date().getTime().toString(),
			name: overrides?.name || 'New Package',
			creativeUnits: [],
			layers: [],
			customizations: [],
			styles: CreativePackageUtils.defaultStyles,
			variables: {},
			animation: {},
			exportTypes: [ExportType.STATIC],
			advancedConfig: {
				disableCustomSizes: true
			},
			isPinned: false,
			...overrides
		};

		if (cloneFrom) {
			schema = {
				...schema,
				creativeUnits: cloneFrom.creativeUnits,
				layers: cloneFrom.layers,
				customizations: cloneFrom.customizations,
				styles: cloneFrom.styles,
				variables: cloneFrom.variables,
				animationConfig: cloneFrom.animationConfig,
				interactivity: cloneFrom.interactivity,
				disableLasso: cloneFrom.disableLasso,
				printConfig: cloneFrom.printConfig,
				advancedConfig: cloneFrom.advancedConfig
			};
		}

		return schema;
	}

	/**
	 * Create a Creative Unit Layer
	 */
	public static createCreativeUnitLayer(overrides: any, interactivityEnabled: boolean, enableDefaultStyles?: boolean, enableStyles?: boolean): LayerDto {

		let schema: Partial<LayerDto> = {
			id: overrides?.id || new Date().getTime().toString(),
			label: overrides.label || 'New Layer',
			type: overrides.type || 'text',
			creativeUnitId: overrides.creativeUnitId || undefined,
			style: enableStyles ?
				overrides.style :
				(enableDefaultStyles ? this.getDefaultStyles(overrides.type === 'smart-text' ? 'text' : overrides.type) : null)
		};

		if (overrides.type === 'text' || overrides.type === 'smart-text') {
			schema.text = {
				content: 'Text'
			};
		}

		if (overrides.type === 'image') {
			schema = {
				...schema,
				image: {
					assetPath: enableStyles ? (overrides.image?.assetPath ?? 'https://placehold.co/600x600/png?text=Image%20Placeholder') : 'https://placehold.co/600x600/png?text=Image%20Placeholder'
				},
				style: {
					...schema.style,
					objectFit: 'cover'
				}
			} as LayerDto;
		}

		if (overrides.type === 'smart-text' || (overrides.type === LayerType.TEXT && overrides.text?.smart)) {
			// Change this back to a text type.
			// Make it smart.
			overrides = {
				...overrides,
				type: LayerType.TEXT,
				text: {
					...overrides.text,
					smart: true
				}
			};
		}

		if (overrides.type === 'button') {
			overrides = {
				...overrides,
				type: LayerType.BUTTON,
				text: {
					content: 'Button',
					smart: true
				}
			};

			// Merge default styles with overrides to preserve layout properties (width, height, position, etc.)
			schema.style = enableStyles
				? { ...this.getDefaultStyles(LayerType.BUTTON), ...overrides.style }
				: this.getDefaultStyles(LayerType.BUTTON);
		}

		if (overrides.type === 'shape') {
			overrides = {
				...overrides,
				type: LayerType.SHAPE,
				shape: enableStyles ? (overrides.shape ?? shapeGallery[0]) : shapeGallery[0]
			};

			// Merge default styles with overrides to preserve layout properties (width, height, position, etc.)
			schema.style = enableStyles
				? { ...this.getDefaultStyles(LayerType.SHAPE), ...overrides.style }
				: this.getDefaultStyles(LayerType.SHAPE);
		}

		if (overrides.type === 'video') {
			schema.video = {
				assetPath: overrides.video?.assetPath ?? "https://videoencoderstack-videoencoder5499e89e-1a5uzj84axall.s3.us-east-2.amazonaws.com/placeholder-video.mp4",
				autoPlay: true,
				loop: false,
				muted: true
			} as LayerDto['video'];
		}

		if (overrides.type === 'audio' || overrides.type === 'video') {
			schema.audioConfig = {
				volumeTag: 'music',
				integratedLoudness: -24,
				loudnessRange: 7,
				maxTruePeak: -2
			} as LayerDto['audioConfig'];
		}

		if (overrides.type === 'frame' || overrides.type === 'group' || overrides.type === 'selectionGroup') {
			schema['layers'] = [];
			schema['group'] = {
				flow: 'legacy',
				layout: createLayoutGroupDefaults()
			} as LayerGroupDto & { layout: ReturnType<typeof createLayoutGroupDefaults> };
		}

		// we need this before merge
		if (!enableStyles && (!enableDefaultStyles || !interactivityEnabled)) {
			delete overrides.style;
		}

		return merge(schema, overrides);
	}

	public static getDefaultLayoutStyles(type: LayerDto['type']): any {
		let styles: Partial<LayerDto['style']> = {
			display: 'block',
			position: 'absolute',
			overflow: 'hidden',
			width: '20cqw',
			height: '20cqh',
			left: '0',
			top: '0',
			bottom: 'auto',
			right: 'auto',
			zIndex: 50
		};

		if (type === 'text') {
			styles = {
				...styles,
				overflow: 'visible'
			};
		}

		if (type === 'button') {
			styles = {
				...styles,
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				width: '15cqmax',
				height: '7cqmax',
				containerType: 'size',
				boxSizing: 'border-box',
				fontSize: '10cqh',
				paddingTop: '10cqh',
				paddingBottom: '10cqh',
				paddingLeft: '15cqw',
				paddingRight: '15cqw',
				overflow: 'hidden'
			};
		}

		if (type === 'shape') {
			styles = {
				...styles,
				boxSizing: 'border-box'
			};
		}

		if (type === 'group') {
			styles = {
				...styles,
				containerType: 'size'
			};
		}

		return styles;
	}

	public static getDefaultVisualStyles(type: LayerDto['type']): any {
		let styles: Partial<LayerDto['style']> = {};

		if (type === 'text') {
			styles = {
				...styles,
				color: '#FFF'
			};
		}

		if (type === 'button') {
			styles = {
				...styles,
				backgroundColor: '#000000',
				color: '#ffffff',
				borderRadius: '2cqw',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap'
			};
		}

		return styles;
	}

	public static getDefaultStyles(type: LayerDto['type']): any {
		return {
			...this.getDefaultLayoutStyles(type),
			...this.getDefaultVisualStyles(type),
		} as Partial<LayerDto['style']>;
	}

	/**
	 * Create a Creative Unit Customization.
	 */
	public static createCreativeUnitCustomization(overrides: any): CustomizationItemDto {
		let schema: Partial<CustomizationItemDto> = {
			id: overrides?.id || new Date().getTime().toString(),
			type: overrides.type || 'text',
			label: overrides.label || 'New Customization',
			options: undefined
		};

		if (
			overrides.type === 'text' ||
			overrides.type === 'image-picker' ||
			overrides.type === 'video-picker' ||
			overrides.type === 'color-picker'
		) {
			schema.layerIds = [];
		}

		if (overrides.type === 'select') {
			schema.options = [];
		}

		if (overrides.type === 'toggle') {
			schema.options = [
				{
					id: 'true',
					label: 'True',
					value: true.toString(),
					layers: []
				},
				{
					id: 'false',
					label: 'False',
					value: false.toString(),
					layers: []
				}
			];
		}

		if (overrides.type === 'toggle') {
			schema.label = 'New Toggle';
		}

		if (overrides.type === 'image-picker') {
			schema.label = 'New Image Picker';

			schema['dataSources'] = ['uploads'];

			schema['showSelectButton'] = true;

			schema['selectButtonLabel'] = 'Select Image';
		}

		if (overrides.type === 'video-picker') {
			schema.label = 'New Video Picker';

			schema['dataSources'] = ['uploads'];
		}

		if (overrides.type === 'audio-picker') {
			schema.label = 'New Audio Picker';

			schema['dataSources'] = ['uploads'];
		}

		if (overrides.type === 'color-picker') {
			schema.label = 'New Color Picker';
			schema.options = [];
			schema['userColors'] = [];
		}

                if (overrides.type === 'frame' || overrides.type === 'group' || overrides.type === 'selectionGroup') {
                        schema['children'] = [];
                }

		return {
			...schema,
			...overrides
		};
	}

	/**
	 * Duplicate a Creative Unit Customization.
	 */
	public static duplicateCreativeUnitCustomization(customization: any): CustomizationItemDto {
		const id = new Date().getTime().toString();

		return {
			...customization,
			id: id,
			label: (customization?.label || 'Customization') + '-copy',
			children: [
				...(customization?.children?.map((child: CustomizationItemDto, index) => {
					return {
						...child,
						label: (child?.label || 'Child') + '-copy',
						id: id + '/' + index
					} as CustomizationItemDto;
				}) || [])
			]
		} as CustomizationItemDto;
	}

	public static createStylesContainer(overrides: Partial<StylesContainerConfig>): StylesContainerConfig {
		return {
			id: overrides.id || new Date().getTime().toString(),
			name: overrides.name || 'Tab',
			styles: overrides.styles || ''
		};
	}

	/**
	 * Creates an Animation Timeline Marker.
	 */
	public static createMarker(overrides: any): any {
		let schema: any = {
			id: overrides?.id || new Date().getTime().toString(),
			label: overrides.label || 'New Marker',
			slug: stringToSlug(overrides.label || 'New Marker'),
			time: overrides.time || 0
		};

		return schema;
	}

	public static duplicateAndReplaceIds(obj: any): any {
		// Check if obj is array
		if (Array.isArray(obj)) {
			// recursively duplicate and replace ids for each item
			return obj.map((item: any) => this.duplicateAndReplaceIds(item));
		}

		// check if it's an object
		if (typeof obj === 'object' && obj !== null) {
			// create a new object to store the duplicated data
			const newObj: any = {};

			// go through the keys
			for (const key in obj) {
				if (obj.hasOwnProperty(key)) {
					// if key replace the value
					if (key === 'id') {
						newObj[key] = this.generateNewId();

						// if not key recurse through the key value
					} else {
						newObj[key] = this.duplicateAndReplaceIds(obj[key]);
					}
				}
			}

			return newObj;
		}

		// if object is not an array or an object, return the value
		return obj;
	}

	public static createPatternArray(pattern: string): number[] {
		const kmp: number[] = [];
		let i = 0,
			j = -1;
		kmp[0] = -1;
		while (i < pattern.length) {
			while (j >= 0 && pattern[i] !== pattern[j]) {
				j = kmp[j];
			}
			i++;
			j++;
			kmp[i] = j;
		}
		return kmp;
	}

	/**
	 * Creates a mock project.  Mostly used in the editor to simulate for the preview mode.
	 * @param overrides
	 * @returns
	 */
	public static createMockProject(overrides: any): any {
		return {
			id: overrides?.id || new Date().getTime().toString(),
			name: overrides.name || 'Test Project',
			status: 'draft',
			created: new Date(),
			metadata: {},
			business: {},
			user: {},
			cartItems: undefined,
			...overrides
		};
	}

	public static isPatternInString(pattern: string, text: string) {
		/*
		 * Implemented with Knuth-Morris-Pratt algorithm because visual lag
		 * is experienced when using javascript includes api or brute force comparison
		 * when looking for a pattern on a really large string
		 * */
		if (pattern && text && pattern.length > 0 && text.length > 0) {
			const patternArray = this.createPatternArray(pattern);
			let i = 0,
				j = 0;

			const matches = [];
			while (i < text.length) {
				while (j >= 0 && text[i] != pattern[j]) {
					j = patternArray[j];
				}

				i++;
				j++;
				if (j == pattern.length) {
					return true;
				}
			}

			return matches.length > 0;
		}
		return false;
	}

	public static getFullCreativePackageStyleString(creativePackage: CreativeUnitPackageDto): string {
		if (!creativePackage) {
			return '';
		}

		let result: string = '';

		if (creativePackage.styles) {
			if (typeof creativePackage.styles == 'string') {
				result += creativePackage.styles;
			} else if (Array.isArray(creativePackage.styles) && creativePackage.styles.length > 0) {
				creativePackage.styles.forEach(styleContainer => {
					result += styleContainer.styles;
				});
			}
		}
		if (creativePackage.creativeUnits) {
			creativePackage.creativeUnits.forEach((unit: CreativeUnitDto) => {
				if (unit.styles && unit.styles.length > 0) {
					if (typeof unit.styles == 'string') {
						result += unit.styles;
					} else if (Array.isArray(unit.styles) && unit.styles.length > 0) {
						unit.styles.forEach(styleContainer => {
							result += styleContainer.styles;
						});
					}
				}
				if (unit.layers && unit.layers.length > 0) {
					unit.layers.forEach((layer: LayerDto) => {
						if (layer && layer?.style) {
							result += JSON.stringify(layer?.style);
						}
					});
				}
			});
		}
		if (creativePackage.layers) {
			creativePackage.layers.forEach((layer: LayerDto) => {
				if (layer?.style) {
					result += JSON.stringify(layer?.style);
				}
			});
		}

		return result;
	}

	public static generateNewId(): string {
		return 'id_' + Math.floor(Math.random() * 1000);
	}

	public static duplicateLayer(layer: LayerDto, layers: LayerDto[], parentId?: string, interactivityEnabled?: boolean, enableDefaultStyles?: boolean, duplicateOptions?: LayerDuplicateOptions): {duplicatedLayer: LayerDto, updatedLayers: LayerDto[]} {
		const unfrozenLayer = unfreeze(layer);
		const id = duplicateOptions?.idsMap?.[layer.id] || `${new Date().getTime().toString()}${Math.floor(Math.random() * 1000)}`; // Generate a unique ID based on timestamp and random number

		const creativeUnitIdOverride = (duplicateOptions?.customOverrides && 'creativeUnitId' in duplicateOptions.customOverrides)  ? { creativeUnitId: duplicateOptions.customOverrides['creativeUnitId'] } : {};

		let overrides: LayerDto;

		if (duplicateOptions?.idsMap) {
			// Use the provided IDs map for duplication
			overrides = {
				...unfrozenLayer,
				id: id,
				layers: unfrozenLayer.layers?.length ?
					LayersUtils.mapAllLayers(unfrozenLayer.layers, (layer, index) => {
						const newId = duplicateOptions.idsMap[layer.id] || (id + '-' + index);
						return {
							...layer,
							...creativeUnitIdOverride,
							id: newId
						}
					}) : undefined,
				...(duplicateOptions?.customOverrides || {})
			}
		} else {
			overrides = {
				...unfrozenLayer,
				id: id,
				label: unfrozenLayer.label + ' Copy',
				layers: unfrozenLayer.layers?.length ?
					LayersUtils.mapAllLayersWithIdNesting(unfrozenLayer.layers, (layer) => {
						return {
							...layer,
							label: layer.label,
							...creativeUnitIdOverride
						}
					}, id + '-') : undefined
				,
				...(duplicateOptions?.customOverrides || {})
			}
		}

		let duplicatedLayer: LayerDto;


		if (duplicateOptions?.withProperties){
			duplicatedLayer = {
				...overrides,
				layers: overrides.layers?.map(childLayer => ({
					...childLayer,
					...(duplicateOptions?.customOverrides || {})
				}))
			};
		}else {
			duplicatedLayer = AdUtils.createCreativeUnitLayer(overrides, interactivityEnabled, enableDefaultStyles, duplicateOptions?.withStyles);
		}



		let updatedLayers: LayerDto[];
		if (parentId) {
			updatedLayers = LayersUtils.mapAllLayers(layers, (layer) => {
				if (layer.id === parentId) {
					return {
						...layer,
						layers: [...layer.layers, duplicatedLayer]
					}
				} else {
					return layer;
				}
			});
		}else {
			updatedLayers =  [...layers, duplicatedLayer];
		}

		return {
			duplicatedLayer,
			updatedLayers
		}
	}

	/**	Duplicates a package-level layer across the entire creative unit package.
	 * This method:
	 * - Duplicates the given {@link LayerDto} in the package's top-level `layers` array.
	 * - Builds a mapping of original layer IDs to duplicated layer IDs (including nested layers).
	 * - Uses that ID map to duplicate the corresponding layer and its nested layers in every creative unit (`pkg.creativeUnits`), keeping IDs consistent between the package definition and all unit instances.
	 * @param pkg The creative unit package whose layers and creative units will be updated.
	 * @param layer The package layer to duplicate; the same logical layer will be duplicated on all units.
	 * @param parentId Optional ID of the parent layer or group under which the duplicated layer should be inserted.
	 * @returns An object containing:
	 * - `updatedPackage`: the package with updated `layers` and `creativeUnits`.
	 * - `duplicatedLayer`: the duplicated package-level layer created from the original.
	 */
	public static duplicatePackageLayerOnAllUnits(pkg: CreativeUnitPackageDto, layer: LayerDto, parentId?: string): { updatedPackage: CreativeUnitPackageDto, duplicatedLayer: LayerDto } {
		const layerToDuplicate = LayersUtils.findLayer(pkg.layers, l => l.id === layer.id);
		const {duplicatedLayer, updatedLayers} = this.duplicateLayer(layerToDuplicate, pkg.layers, parentId, !!pkg.interactivity?.enabled, false, {withStyles: false, withProperties: true});

		// prepare ids map for all nested layers
		const idsMap: {[key: string]: string} = {};
		idsMap[layerToDuplicate.id] = duplicatedLayer.id;

		const flattenedOriginalLayers = LayersUtils.flattenLayers(layerToDuplicate?.layers) || [];
		const flattenedDuplicatedLayers = LayersUtils.flattenLayers(duplicatedLayer?.layers) || [];

		flattenedOriginalLayers.forEach((originalLayer, index) => {
			const duplicatedLayer = flattenedDuplicatedLayers[index];
			if (originalLayer && duplicatedLayer) {
				idsMap[originalLayer.id] = duplicatedLayer.id;
			}
		});


		const updatedPackage = {
			...pkg,
			layers: updatedLayers,
			creativeUnits: pkg.creativeUnits.map(unit => {
				const layerToDuplicate = LayersUtils.findLayer(unit.layers, l => l.id === layer.id);
				if (!layerToDuplicate) {
					return unit;
				}
				const {duplicatedLayer, updatedLayers} = AdUtils.duplicateLayer(layerToDuplicate, unit.layers, parentId, !!pkg.interactivity?.enabled, false, {withStyles: false, withProperties: true, idsMap});
				return {
					...unit,
					layers: updatedLayers
				}
			})
		};

		return { updatedPackage, duplicatedLayer }

	}

	/**
	 * Calculates the bounds that encompass all children of a group layer recursively.
	 * This is used for both group layers and combined bounding boxes.
	 *
	 * @param layers - The layers to calculate bounds for
	 * @param containerElement - The container element for offset calculations
	 * @param getElementByLayerId - Function to get element by layer ID
	 * @returns The calculated bounds or null if no valid bounds found
	 */
	public static calculateDeepBounds(
		layers: LayerDto[],
		containerElement: HTMLElement,
		getElementByLayerId: (id: string) => HTMLElement | null
	): { minLeft: number; minTop: number; maxRight: number; maxBottom: number } | null {
		let minLeft = Number.POSITIVE_INFINITY;
		let minTop = Number.POSITIVE_INFINITY;
		let maxRight = Number.NEGATIVE_INFINITY;
		let maxBottom = Number.NEGATIVE_INFINITY;
		let hasValidBounds = false;

		const processLayer = (layer: LayerDto) => {
			// If the layer has children, process them recursively
			if (layer.layers && layer.layers.length > 0) {
				layer.layers.forEach(processLayer);
			} else {
				// This is a leaf layer, calculate its bounds
				const element = getElementByLayerId(layer.id);
				if (element) {
					const bounds = this.getElementBounds(element, containerElement);
					if (bounds) {
						minLeft = Math.min(minLeft, bounds.left);
						minTop = Math.min(minTop, bounds.top);
						maxRight = Math.max(maxRight, bounds.right);
						maxBottom = Math.max(maxBottom, bounds.bottom);
						hasValidBounds = true;
					}
				}
			}
		};

		layers.forEach(processLayer);

		return hasValidBounds ? { minLeft, minTop, maxRight, maxBottom } : null;
	}

	/**
	 * Gets the effective bounds of an element, accounting for rotation and transforms.
	 *
	 * @param element - The HTML element to get bounds for
	 * @param containerElement - The container element for offset calculations
	 * @returns The effective visual bounds
	 */
	private static getElementBounds(
		element: HTMLElement,
		containerElement: HTMLElement
	): { left: number; top: number; right: number; bottom: number } | null {
		if (!element || !containerElement) return null;

		// Get the bounding client rect which accounts for transforms
		const rect = element.getBoundingClientRect();
		const containerRect = containerElement.getBoundingClientRect();

		return {
			left: rect.left - containerRect.left,
			top: rect.top - containerRect.top,
			right: rect.right - containerRect.left,
			bottom: rect.bottom - containerRect.top
		};
	}
}
